/**
 * PhotoThumbnail — single-photo thumbnail rendered via usePhotoSrc.
 *
 * Renders three states per spec (Phase 4.3):
 *  - loading: bark-700 placeholder + animate-pulse + muted ImageIcon
 *  - error:   bark-700 + ImageOff + "Photo unavailable" + retry on tap
 *  - ready:   <img> with the hydrated src
 *
 * Surfaces data-photo-state on the wrapper for semantic test assertions (RULE-096).
 * Revokes the object URL on unmount via usePhotoSrc cleanup.
 */

import { ImageIcon, ImageOff } from 'lucide-react';

import { usePhotoSrc } from '@/hooks/use-photo-src';

// ---------------------------------------------------------------------------
// Named constants (RULE-153)
// ---------------------------------------------------------------------------

/** Minimum pixel height for the photo placeholder skeleton (loading / idle states). */
export const PHOTO_PLACEHOLDER_HEIGHT_PX = 80;

export interface PhotoThumbnailProps {
  photoId: string;
  alt?: string;
  className?: string;
  /** Extra classes applied to the wrapper div */
  wrapperClassName?: string;
  onClick?: () => void;
  'aria-label'?: string;
  'data-testid'?: string;
}

export function PhotoThumbnail({
  photoId,
  alt = 'Photo',
  className = 'w-full h-full object-cover',
  wrapperClassName = '',
  onClick,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: PhotoThumbnailProps) {
  const { src, state, retry } = usePhotoSrc(photoId);

  const wrapper = (
    <div
      className={`relative w-full h-full ${wrapperClassName}`}
      data-photo-state={state}
      data-testid={testId}
    >
      {state === 'loading' && (
        <div className="w-full h-full bg-bark-700 animate-pulse flex items-center justify-center">
          <ImageIcon size={20} className="text-bark-500" aria-hidden />
        </div>
      )}

      {state === 'error' && (
        <button
          type="button"
          onClick={retry}
          className="w-full h-full bg-bark-700 flex flex-col items-center justify-center gap-1 text-bark-500 hover:text-bark-400 transition-colors cursor-pointer"
          aria-label="Photo unavailable - tap to retry"
        >
          <ImageOff size={16} aria-hidden />
          <span className="text-body-xs">Photo unavailable</span>
        </button>
      )}

      {state === 'ready' && src && (
        <img
          src={src}
          alt={alt}
          className={className}
        />
      )}

      {/* idle state: empty placeholder */}
      {state === 'idle' && (
        <div className="w-full h-full bg-bark-700" />
      )}
    </div>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        className="w-full h-full block"
        data-testid="photo-clickable-wrapper"
      >
        {wrapper}
      </button>
    );
  }

  return wrapper;
}
